import { Input, Textarea } from '@/components/ui';
import { QuestionWrapper } from './question-wrapper';
import type { QuestionComponentProps } from '@/types';

export function OpenText({
  question,
  value,
  onChange,
  disabled,
  errors,
}: QuestionComponentProps<string | null>) {
  const isMultiline = question.config?.multiline ?? true;
  const placeholder = question.config?.placeholder || 'Enter your answer...';
  const maxLength = question.config?.maxLength;
  const preventCopyPaste = question.config?.preventCopyPaste ?? false;
  const autoTrimWhitespace = question.config?.autoTrimWhitespace ?? false;

  const currentLength = typeof value === 'string' ? value.length : 0;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newValue = e.target.value;
    onChange(newValue || null);
  };

  // Block paste when configured. We don't mutate typed input.
  const handlePaste = (e: React.ClipboardEvent) => {
    if (preventCopyPaste) e.preventDefault();
  };

  // Trim leading/trailing whitespace on blur only — never mid-keystroke, so we
  // don't fight a respondent typing a space between words.
  const handleBlur = () => {
    if (!autoTrimWhitespace || typeof value !== 'string') return;
    const trimmed = value.trim();
    if (trimmed !== value) onChange(trimmed || null);
  };

  return (
    <QuestionWrapper question={question} errors={errors}>
      {isMultiline ? (
        <Textarea
          value={value || ''}
          onChange={handleChange}
          onPaste={handlePaste}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          hasError={!!errors?.length}
          maxLength={maxLength}
          className="max-w-xl"
        />
      ) : (
        <Input
          type="text"
          value={value || ''}
          onChange={handleChange}
          onPaste={handlePaste}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          hasError={!!errors?.length}
          maxLength={maxLength}
          className="max-w-xl"
        />
      )}

      {/* Character count */}
      {maxLength && (
        <p className="mt-1 text-xs text-gray-500 text-right max-w-xl">
          {currentLength} / {maxLength} characters
        </p>
      )}
    </QuestionWrapper>
  );
}
